// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import { FeatureCollection } from 'geojson';

import Select2, { DataSelect2 } from 'wdna-select2';

import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { setFilterData } from 'wdna-components/lib/mainfilter/actions';
import { messageError, messageWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { ContextType, FilterContext } from '../../../../../types';
import { sensor, SensorListDO } from '../../../../sensors/requests';

interface Props {
  rowId?: number;
}

interface State {
  id?: number;
  sensorArray: SensorListDO[];
}

export default class Filter extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private firstRender: boolean;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.firstRender = true;

    this.state = {
      sensorArray: []
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load();
  };

  public componentDidUpdate(): void {
    const { firstRender } = this;
    if (firstRender) {
      this.firstRender = false;
      this.exportInitialData();
    }
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = (): void => {
    const filter: Record<string, any> = {};
    filter.status = 'ON_AIR';

    const idSensor: number | undefined = this.props.rowId;
    if (idSensor) filter.id = idSensor;

    this.loadingStart();
    sensor<SensorListDO[]>('list', undefined, filter)
      ?.then((resp: SensorListDO[]) => {
        if (resp && resp.length > 0) {
          this.setState({
            sensorArray: resp,
            id: idSensor,
          });
        } else {
          messageWarning('Warning', 'There are not active sensors in the system');
        }
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public submit = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    const req: Record<string, any> = {};
    if (this.state.id && this.state.id > 0) {
      req.id = this.state.id;
    }

    if (!this.chkFilter(req)) return;

    this.loadingStart();
    sensor<FeatureCollection>('gis_position', undefined, req)
      ?.then((resp: FeatureCollection) => {
        if (this.mounted) {
          this.readingsLoad(resp);
        }
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public readingsLoad = (mapData: FeatureCollection): void => {
    this.context?.updateParent({
      update: window.getStr(),
      loading: true,
      mapData,
    });
  };

  public chkFilter = (_data: Record<string, any>): boolean => {
    return true;
  };

  public loadingStart = (): void => {
    this.context?.updateParent({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.context?.updateParent({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public exportInitialData = (): void => {
    this.setState({}, () => {
      this.notifyFilterChange();
      this.exportMainFilter();
    });
  };

  public exportMainFilter = (): void => {
    const mainFilter: any = this.getFilterState();

    this.context?.updateParent({
      mainFilter,
    });

    window.store.dispatch(setFilterData(mainFilter));
  };

  public notifyFilterChange = (): void => {
    this.context?.updateParent({
      onFilterChange: this.getFilterState(),
    });
  };

  public getFilterState = (): any => {
    const { state } = this;

    const mainFilter: any = _.cloneDeep(state);

    /* clean the filter */
    return mainFilter;
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value, type, checked } = (e as React.ChangeEvent<HTMLInputElement>).target;
    if (type === 'checkbox') {
      this.setState((prevState: State) => ({
        ...prevState,
        [name]: checked,
      }));
    } else {
      this.setState((prevState: State) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  public render(): JSX.Element {
    const { sensorArray } = this.state;

    const sensors: DataSelect2[] = sensorArray
      .filter((item: SensorListDO) => item.latitude != null)
      .map((item: SensorListDO): DataSelect2 => {
        return {
          title: `${item.code} - ${item.name}`,
          text: `${item.description}`,
          id: item.id,
          'data-class': 'select2-results__option2'
        };
      });

    // Default option all sensors
    sensors.unshift({ text: 'ALL SENSORS', id: 0 });

    return (
      <div className="main-filters">
        <UiValidate>
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="id"
              data={sensors}
              value={this.state.id}
              options={{
                placeholder: 'ALL SENSORS',
              }}
              onChange={this.handleChange}
              defaultValue={0}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ marginTop: '1px' }}>
            <button type="button" className="filter-btn btn btn-default" onClick={this.submit}>
              <i className="wdna-ico-icon-playsearch" />
              <span>GO</span>
            </button>
          </div>
        </UiValidate>
      </div>
    );
  }
}
